import { ReactNode } from 'react'

import { AstraParagraph, AstraSkeletonLoader, AstraTooltip, astraTwMerge } from '~/astra'

export type BarListItem = {
    name: string
    actual: number
    total: number
    tooltip?: string
}

type Props = {
    title: ReactNode
    unit: string
    items: BarListItem[]
    isLoading?: boolean
    loadingCount?: number
    interactive?: boolean
    onClick?: (item: BarListItem) => void
}

const LoadingBarList = () => (
    <div className="mb-2 flex justify-between">
        <div className="h-7 w-3/4">
            <AstraSkeletonLoader />
        </div>
        <div className="h-7 w-7">
            <AstraSkeletonLoader />
        </div>
    </div>
)

export const LoadingBarListArray = ({ length }: { length: number }) =>
    Array.from({ length }, (_, index) => <LoadingBarList key={`loading-bar-list-${index}`} />)

export const BarList = ({ title, unit, items, isLoading, loadingCount = 6, interactive, onClick }: Props) => {
    const biggestTotal = items.reduce((acc, item) => Math.max(acc, item.total), 0)
    const sortedByTotal = items.sort((a, b) => b.total - a.total || b.actual - a.actual)

    const gridItemBase = 'col-start-1 row-start-1 rounded'
    const overlayClasses = `${gridItemBase} col-span-full px-2 py-1`

    return (
        <div className="flex flex-col gap-2 overflow-auto rounded-md border bg-surface-base p-4">
            <div className="flex items-baseline justify-between gap-2">
                <AstraParagraph variant="body-small" weight="medium">
                    {title}
                </AstraParagraph>
                <AstraParagraph variant="body-small" weight="medium">
                    {unit}
                </AstraParagraph>
            </div>
            <div className={`flex flex-col overflow-auto ${interactive ? '' : 'gap-y-2'}`}>
                {isLoading ? (
                    <LoadingBarListArray length={loadingCount} />
                ) : (
                    <>
                        {sortedByTotal.map(item => (
                            <div
                                key={item.name}
                                data-test="bar-list-item"
                                className={astraTwMerge(
                                    'flex items-center justify-between gap-x-6',
                                    interactive ? 'cursor-pointer rounded py-1 hover:bg-surface-muted' : ''
                                )}
                                onClick={() => onClick?.(item)}
                            >
                                <div
                                    className="grid h-full w-full flex-1 grid-rows-1"
                                    style={{ gridTemplateColumns: `repeat(${biggestTotal}, minmax(0, 1fr))` }}
                                >
                                    {item.total !== 0 && <div className={`${gridItemBase} bg-[#DEF7F9]`} style={{ gridColumnEnd: `span ${item.total}` }} />}
                                    {item.actual !== 0 && <div className={`${gridItemBase} bg-[#9DDDE7]`} style={{ gridColumnEnd: `span ${item.actual}` }} />}
                                    <div className={overlayClasses}>
                                        <AstraTooltip tooltipContent={item.tooltip}>
                                            <AstraParagraph variant="body" className="w-fit">
                                                {item.name}
                                            </AstraParagraph>
                                        </AstraTooltip>
                                    </div>
                                </div>
                                <AstraParagraph className="flex-shrink" variant="body">
                                    {item.actual}/{item.total}
                                </AstraParagraph>
                            </div>
                        ))}
                        <div data-test="bar-list-loaded" className="hidden" />
                    </>
                )}
            </div>
        </div>
    )
}
